import { Icon } from '@advanza/advanza_generic'
import { Col, Row } from '@advanza/grid'
import { SelectSimple } from '@advanza/input'
import React from 'react'
import LabelPicker from '../labels/LabelPicker'
import ServicesSelect from '../services/ServicesSelect'
import './eligibilityRulesInput.module.css'

const KEY_OPTIONS = [
    { value: 'ProspectValue', name: 'ProspectValue' },
    { value: 'services', name: 'services' },
]

const OPERATOR_OPTIONS = [
    { value: '>', name: '>' },
    { value: '>=', name: '>=' },
    { value: '=', name: '=' },
    { value: '<', name: '<' },
    { value: '<=', name: '<=' },
]

const SERVICES_OPERATOR_OPTIONS = [
    { value: '=', name: 'is' },
    { value: '!=', name: 'is not' },
]

const PROSPECT_VALUE_OPERATOR_OPTIONS = [
    { value: '=', name: 'is' },
    { value: '!=', name: 'is not' },
]

/**
 * Component for managing eligibility rules
 * Each rule has: key (isSigned/ProspectValue), operator (>, >=, =, <, <=), and value
 * Converts between JSON format and array of rule objects
 */
class EligibilityRulesInput extends React.Component {
    constructor(props) {
        super(props)
        const parsedRules = this.parseRules(props.value || '')
        // Add unique IDs to each rule for stable React keys
        const rulesWithIds = parsedRules.map((rule, index) => ({
            ...rule,
            _id: `rule_${Date.now()}_${index}_${Math.random()}`,
        }))
        this.state = {
            rules: rulesWithIds,
        }
    }

    componentDidUpdate(prevProps) {
        // Update internal state when value prop changes from external source
        if (prevProps.value !== this.props.value) {
            // Normalize both values for comparison
            const normalizeValue = (val) => {
                if (!val) return ''
                if (Array.isArray(val) || typeof val === 'object') {
                    return JSON.stringify(val)
                }
                return String(val)
            }

            const currentRulesJson = this.buildRulesJson(this.state.rules)
            const normalizedCurrent = normalizeValue(currentRulesJson)
            const normalizedIncoming = normalizeValue(this.props.value)

            // Only update if the incoming value is different from what we would generate
            if (normalizedIncoming !== normalizedCurrent) {
                const parsedRules = this.parseRules(this.props.value || '')
                // Preserve existing IDs or generate new ones
                const rulesWithIds = parsedRules.map((rule, index) => {
                    const existingRule = this.state.rules[index]
                    return {
                        ...rule,
                        _id: existingRule?._id || `rule_${Date.now()}_${index}_${Math.random()}`,
                    }
                })
                this.setState({
                    rules: rulesWithIds,
                })
            }
        }
    }

    parseRules(rulesJson) {
        // JSON columns are automatically decoded to arrays/objects by CakePHP
        // Handle arrays/objects directly, no string parsing needed
        if (!rulesJson) {
            return []
        }

        // If it's already an array, use it directly
        if (Array.isArray(rulesJson)) {
            // Filter out userType, specificProviders, and premiumSignedDate rules - userType is now in general settings, premiumSignedDate is removed
            return rulesJson.filter(
                (rule) =>
                    rule.key !== 'specificProviders' &&
                    rule.key !== 'userType' &&
                    rule.key !== 'premiumSignedDate',
            )
        }

        // If it's an object (but not an array), wrap it in an array
        if (typeof rulesJson === 'object') {
            // Filter out userType, specificProviders, and premiumSignedDate rules
            if (
                rulesJson.key === 'specificProviders' ||
                rulesJson.key === 'userType' ||
                rulesJson.key === 'premiumSignedDate'
            ) {
                return []
            }
            return [rulesJson]
        }

        // Legacy string format support (fallback)
        if (typeof rulesJson === 'string') {
            try {
                const parsed = JSON.parse(rulesJson)
                if (Array.isArray(parsed)) {
                    // Filter out userType, specificProviders, and premiumSignedDate rules - userType is now in general settings, premiumSignedDate is removed
                    return parsed.filter(
                        (rule) =>
                            rule.key !== 'specificProviders' &&
                            rule.key !== 'userType' &&
                            rule.key !== 'premiumSignedDate',
                    )
                }
                return []
            } catch (error) {
                return []
            }
        }

        return []
    }

    buildRulesJson(rules) {
        try {
            // Filter out rules that are completely empty (no key) or incomplete (missing operator or value)
            // A rule is considered valid if it has a key, operator, and value
            const validRules = rules
                .filter((rule) => {
                    // Must have a key
                    if (!rule.key) {
                        return false
                    }
                    // Must have an operator
                    if (!rule.operator) {
                        return false
                    }
                    // Must have a value (can be empty string for some cases, but not undefined/null)
                    if (rule.value === undefined || rule.value === null) {
                        return false
                    }
                    // For arrays (like ProspectValue), check if it's not empty
                    return !(Array.isArray(rule.value) && rule.value.length === 0)
                })
                .map((rule) => {
                    // Remove _id and _labelDescriptions (internal-only) from the rule object
                    const { _id, _labelDescriptions, ...ruleWithoutId } = rule
                    // Exclude ids field from all rules
                    const { ids, ...ruleWithoutIds } = ruleWithoutId
                    // For ProspectValue, validate operator ('=' or '!=') and ensure value is an array
                    if (ruleWithoutIds.key === 'ProspectValue') {
                        if (ruleWithoutIds.operator !== '=' && ruleWithoutIds.operator !== '!=') {
                            ruleWithoutIds.operator = '='
                        }
                        // Ensure value is always an array
                        if (!Array.isArray(ruleWithoutIds.value)) {
                            ruleWithoutIds.value = ruleWithoutIds.value
                                ? [ruleWithoutIds.value]
                                : []
                        }
                    }
                    return ruleWithoutIds
                })
            // Return array directly - CakePHP's JsonType will encode it automatically
            return validRules.length > 0 ? validRules : []
        } catch (error) {
            return []
        }
    }

    handleRuleChange(index, field, newValue) {
        const rules = [...this.state.rules]
        const oldKey = rules[index]?.key

        // Ensure we have a rule object at this index
        if (!rules[index]) {
            rules[index] = { key: '', operator: '', value: '' }
        }
        // Create a new rule object with the updated field
        const updatedRule = { ...rules[index], [field]: newValue }

        // Clear value and operator when key changes
        if (field === 'key' && oldKey !== newValue) {
            updatedRule.value = ''
            updatedRule.operator = ''
            // If switching to ProspectValue, default to '=' operator (is)
            if (newValue === 'ProspectValue') {
                updatedRule.operator = '='
            } else if (
                newValue === 'services' &&
                updatedRule.operator &&
                updatedRule.operator !== '=' &&
                updatedRule.operator !== '!='
            ) {
                updatedRule.operator = ''
            } else {
                updatedRule.operator = ''
            }
        }

        rules[index] = updatedRule
        this.setState({ rules }, () => {
            // Notify change after state update to ensure proper re-render
            this.notifyChange(rules)
        })
    }

    handleAdd() {
        const newRule = {
            key: 'ProspectValue',
            operator: '',
            value: '',
            _id: `rule_${Date.now()}_${Math.random()}`,
        }
        const rules = [...this.state.rules, newRule]
        this.setState({ rules })
        this.notifyChange(rules)
    }

    handleRemove(index) {
        const rules = this.state.rules.filter((_, i) => i !== index)
        this.setState({ rules })
        this.notifyChange(rules)
    }

    notifyChange(rules) {
        // Build array with only complete rules (has key, operator, and value)
        // Return array directly - CakePHP's JsonType will encode it automatically
        const rulesArray = this.buildRulesJson(rules)
        const { onChange, name } = this.props
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: rulesArray,
                },
            })
        }
    }

    formatRulePreview(rule) {
        const { key, operator, value } = rule
        if (!key) {
            return null
        }

        // ProspectValue requires operator and value
        if (
            key === 'ProspectValue' &&
            (!operator || !value || (Array.isArray(value) && value.length === 0))
        ) {
            return null
        }

        // Other keys require operator
        if (key !== 'ProspectValue' && !operator) {
            return null
        }

        let valueDisplay = ''
        if (value !== undefined && value !== null && value !== '') {
            if (key === 'ProspectValue') {
                if (Array.isArray(value)) {
                    valueDisplay = value.map((id) => rule._labelDescriptions?.[id] ?? id).join(', ')
                } else {
                    valueDisplay = rule._labelDescriptions?.[value] ?? value.toString()
                }
                const operatorDisplay = operator === '!=' ? 'is not' : 'is'
                return `ProspectValue ${operatorDisplay} ${valueDisplay}`
            } else if (key === 'services') {
                // For services, show service ID(s)
                if (Array.isArray(value)) {
                    valueDisplay = `[${value.join(', ')}]`
                } else {
                    valueDisplay = value.toString()
                }
            } else if (key === 'specificProviders') {
                // This should not appear in preview anymore, but handle it just in case
                // For specific providers, show provider IDs
                if (typeof value === 'string') {
                    valueDisplay = value
                } else if (Array.isArray(value)) {
                    valueDisplay = value.join(', ')
                } else {
                    valueDisplay = value.toString()
                }
            } else {
                valueDisplay = value.toString()
            }
        } else {
            return null // Incomplete rule
        }

        return `${key} ${operator} ${valueDisplay}`
    }

    getRulesPreview() {
        const { rules } = this.state
        const previews = rules
            .filter(
                (rule) =>
                    rule.key !== 'specificProviders' &&
                    rule.key !== 'userType' &&
                    rule.key !== 'premiumSignedDate',
            ) // Exclude userType, specificProviders, and premiumSignedDate rules from preview
            .map((rule) => this.formatRulePreview(rule))
            .filter((preview) => preview !== null)
        return previews.join(' AND ')
    }

    renderValueInput(rule, index) {
        const { key, value } = rule

        if (key === 'ProspectValue') {
            // Parse value - can be a single ID, array of IDs, or array of label objects
            let labelIds = []
            if (Array.isArray(value)) {
                labelIds = value.map((item) =>
                    typeof item === 'object' && item.office_label_id ? item.office_label_id : item,
                )
            } else if (value) {
                labelIds = [value]
            }
            return (
                <div style={{ width: '100%', display: 'block' }}>
                    <LabelPicker
                        key={`labelpicker-${index}-${key}`}
                        selectedLabelIds={labelIds}
                        categories={['serviceProviders']}
                        filter={({ type }) => type === 'prospect'}
                        onValueChange={(labels) => {
                            const ids = labels.map((label) => label.office_label_id)
                            const labelDescriptions = {}
                            labels.forEach((label) => {
                                labelDescriptions[label.office_label_id] = label.description
                            })
                            const rules = [...this.state.rules]
                            const updatedRule = {
                                ...rules[index],
                                value: ids,
                                _labelDescriptions: labelDescriptions,
                            }
                            if (ids.length > 0 && !updatedRule.operator) {
                                updatedRule.operator = '='
                            }
                            rules[index] = updatedRule
                            this.setState({ rules }, () => this.notifyChange(rules))
                        }}
                        placeholderAdd="Prospect value"
                        classNames={['select-min', 'select-stealth']}
                    />
                </div>
            )
        } else if (key === 'services') {
            // Parse value - can be a single ID or array of IDs
            let serviceIds = []
            if (Array.isArray(value)) {
                serviceIds = value.map((item) =>
                    typeof item === 'object' && item.service_id ? item.service_id : item,
                )
            } else if (value) {
                serviceIds = [value]
            }
            return (
                <div style={{ width: '100%', display: 'block' }}>
                    <ServicesSelect
                        key={`servicesselect-${index}-${key}`}
                        multiple
                        useSelectedCountry
                        value={serviceIds}
                        onChangeValue={(services) => {
                            // Store service IDs - support single or multiple
                            const ids = services.map((service) =>
                                typeof service === 'object' && service.service_id
                                    ? service.service_id
                                    : service,
                            )
                            this.handleRuleChange(index, 'value', ids.length === 1 ? ids[0] : ids)
                        }}
                        placeholder="Select services"
                    />
                </div>
            )
        }

        return null
    }

    render() {
        const { rules } = this.state
        const { error } = this.props

        return (
            <div>
                {rules.map((rule, index) => {
                    // Remove any userType, specificProviders, and premiumSignedDate rules from the display
                    // (userType is now in general settings, provider IDs are stored in specific_provider_ids field, premiumSignedDate is removed)
                    if (
                        rule.key === 'specificProviders' ||
                        rule.key === 'userType' ||
                        rule.key === 'premiumSignedDate'
                    ) {
                        return null // Don't render userType, specificProviders, or premiumSignedDate rules
                    }

                    // Use unique ID as key to maintain component identity
                    return (
                        <Row
                            key={rule._id || `rule-row-${index}`}
                            className="v-center"
                            style={{
                                marginBottom: '8px',
                            }}>
                            <Col className="grow" style={{ marginRight: '8px' }}>
                                <SelectSimple
                                    placeholder="Key"
                                    value={rule.key || ''}
                                    options={KEY_OPTIONS}
                                    alwaysControlValue
                                    onChange={(e) => {
                                        const newKey = e.target.value
                                        this.handleRuleChange(index, 'key', newKey)
                                    }}
                                    id={`eligibility_rule_${index}_key`}
                                    name={`eligibility_rule_${index}_key`}
                                />
                            </Col>
                            <Col style={{ marginRight: '8px', minWidth: '100px' }}>
                                <SelectSimple
                                    placeholder="Operator"
                                    value={rule.operator || null}
                                    options={
                                        rule.key === 'ProspectValue'
                                            ? PROSPECT_VALUE_OPERATOR_OPTIONS
                                            : rule.key === 'services'
                                              ? SERVICES_OPERATOR_OPTIONS
                                              : OPERATOR_OPTIONS
                                    }
                                    alwaysControlValue
                                    emptyOption="Select operator"
                                    onChange={(e) => {
                                        const newOperator = e.target.value
                                        // SelectSimple converts emptyOption ("-") to null, convert to empty string for consistency
                                        this.handleRuleChange(
                                            index,
                                            'operator',
                                            newOperator === null ||
                                                newOperator === '' ||
                                                newOperator === undefined
                                                ? ''
                                                : newOperator,
                                        )
                                    }}
                                    id={`eligibility_rule_${index}_operator`}
                                    name={`eligibility_rule_${index}_operator`}
                                />
                            </Col>
                            <Col className="grow" style={{ marginRight: '8px' }}>
                                {rule.key ? (
                                    <div key={`value-input-wrapper-${index}-${rule.key}`}>
                                        {this.renderValueInput(rule, index)}
                                    </div>
                                ) : (
                                    <div
                                        key={`value-input-placeholder-${index}`}
                                        style={{ height: '42px' }}
                                    />
                                )}
                            </Col>
                            <Col
                                c="auto"
                                style={{
                                    alignSelf: 'center',
                                }}>
                                <span
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        this.handleRemove(index)
                                    }}
                                    style={{
                                        color: '#1976d2',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '14px',
                                    }}>
                                    Remove
                                </span>
                            </Col>
                        </Row>
                    )
                })}
                {this.props.renderAfterRules}
                <Row>
                    <Col>
                        <button
                            type="button"
                            onClick={() => this.handleAdd()}
                            className="plain c-blue"
                            style={{ marginTop: '8px' }}>
                            <Icon>add</Icon> add rule
                        </button>
                    </Col>
                </Row>
                {error && (
                    <div className="error" style={{ marginTop: '8px' }}>
                        Error: {error}
                    </div>
                )}
            </div>
        )
    }
}

EligibilityRulesInput.propTypes = {}

export default EligibilityRulesInput
